import React from 'react';

import { StyledTextField } from './CustomTextField.style';

/**
 * @component
 * @description Reusable custom text field component built on Material-UI TextField.
 * Provides a standardized input field with consistent styling and behavior across the application.
 * Features custom styling, validation support, and flexible configuration options.
 *
 * Key Features:
 * - Consistent custom styling with theme integration
 * - Built-in error handling and validation display
 * - Support for multiline text areas
 * - Flexible input types (text, email, number, etc.)
 * - Disabled state support
 * - Custom placeholder text
 * - Key press event handling
 * - Auto-complete disabled for security
 * - Responsive design
 * - Helper text support
 * - Full width layout by default
 *
 * @param {Object} props - Component props
 * @param {string} props.name - Input field name attribute
 * @param {string} [props.type='text'] - Input field type (text, email, password, number, etc.)
 * @param {string|number} props.value - Current input value
 * @param {Function} props.onBlur - Blur event handler
 * @param {Function} props.onChange - Change event handler
 * @param {boolean} [props.error=false] - Whether the field has validation errors
 * @param {string} [props.helperText] - Helper text to display below the field
 * @param {boolean} [props.multiline=false] - Whether the field should be multiline
 * @param {number} [props.rows] - Number of rows for multiline fields
 * @param {boolean} [props.disabled=false] - Whether the field is disabled
 * @param {Function} [props.onKeyPress] - Key press event handler
 * @param {string} [props.placeholder] - Placeholder text
 * @param {Object} [props.otherProps] - Additional props to pass to the underlying TextField
 * @returns {JSX.Element} Styled text field component
 *
 * @example
 * // Basic text input
 * <CustomTextField
 *   name="username"
 *   type="text"
 *   value={formValues.username}
 *   onChange={handleChange}
 *   onBlur={handleBlur}
 *   placeholder="Enter username"
 * />
 *
 * @example
 * // Text input with validation
 * <CustomTextField
 *   name="email"
 *   type="email"
 *   value={formValues.email}
 *   onChange={handleChange}
 *   onBlur={handleBlur}
 *   error={!!errors.email}
 *   helperText={errors.email}
 *   placeholder="Enter email address"
 * />
 *
 * @example
 * // Multiline text area
 * <CustomTextField
 *   name="description"
 *   type="text"
 *   value={formValues.description}
 *   onChange={handleChange}
 *   onBlur={handleBlur}
 *   multiline
 *   rows={4}
 *   placeholder="Enter description"
 * />
 *
 * @example
 * // Disabled field
 * <CustomTextField
 *   name="readOnlyField"
 *   type="text"
 *   value={readOnlyValue}
 *   disabled
 * />
 *
 * @see StyledTextField - Custom styled Material-UI TextField
 * @see Material-UI TextField - Base component documentation
 */
const CustomTextField = ({
    name,
    type,
    value,
    onBlur,
    onChange,
    error,
    helperText,
    multiline,
    rows,
    disabled,
    onKeyPress,
    placeholder,
    otherProps
}) => {
    return (
        <StyledTextField
            onKeyDown={onKeyPress}
            size="small"
            placeholder={placeholder}
            fullWidth
            margin="normal"
            variant="outlined"
            id={name}
            name={name}
            autoComplete="off"
            type={type}
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            error={error}
            helperText={helperText}
            multiline={multiline}
            rows={rows}
            style={{ marginTop: '0px' }}
            FormHelperTextProps={{ style: { marginLeft: '0px' } }}
            disabled={disabled}
            {...otherProps}
        />
    );
};

export default CustomTextField;
